/* eslint-disable react/no-array-index-key */
/* eslint-disable no-plusplus */
/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-explicit-any */
import IconAngleBottom from '@assets/icons/angle-bottom.svg?react';
import {
 Accordion,
 AccordionDetails,
 AccordionSummary,
 Box,
 FormGroup,
 Theme,
 Typography,
 useTheme,
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-premium';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import customColors from '../../../utils/customColors';
import { findNormalizedMatchRange } from '../../../utils/stringUtils';
import FieldSearch from '../FieldSearch';
import { PaletteDraggableField } from './DraggableField';
import { TransformedStructure } from './fieldsModalTypes';
import {
 filterStructureForExpanded,
 filterStructureForSearch,
 sortStructure,
} from './utils';

interface ExpandedStateType {
 [key: string]: boolean;
}

const highlightText = (text: string, highlight: string, theme: Theme) => {
 const matchRange = findNormalizedMatchRange(text || '', highlight || '');
 if (!matchRange) {
  return (
   <span
    style={{
     width: '100%',
     display: 'block',
     textOverflow: 'ellipsis',
     overflow: 'hidden',
    }}
   >
    {text}
   </span>
  );
 }
 const start = text.slice(0, matchRange.start);
 const match = text.slice(matchRange.start, matchRange.end);
 const end = text.slice(matchRange.end);
 return (
  <span
   style={{
    width: '100%',
    display: 'block',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
   }}
  >
   {start}
   <span
    style={{
     backgroundColor: customColors.yellow,
     color: 'inherit',
    }}
   >
    {match}
   </span>
   {end}
  </span>
 );
};

const renderFields = ({
 item,
 pName,
 depth,
 searchValue,
 handleSetPreset,
 expandedState,
 setExpandedState,
 theme,
}: {
 item: TransformedStructure;
 pName: string;
 depth: number;
 searchValue: string;
 handleSetPreset: (data: any) => void;
 expandedState: ExpandedStateType;
 setExpandedState: (
  value:
   | ExpandedStateType
   | ((prevState: ExpandedStateType) => ExpandedStateType),
 ) => void;
 theme: Theme;
}) => {
 depth++;
 const currentPath = pName || item.Name;
 if (['model', 'array'].includes(item.Type)) {
  return (
   <Box key={`group${item.Key}`} sx={{ p: 0, width: '100%' }}>
    <Accordion
     sx={{
      borderRadius: '0 !important',
      boxShadow: 'none',
     }}
     expanded={expandedState?.[`${depth}_${item.Name}`] || false}
     onChange={() => {
      setExpandedState((prevState: ExpandedStateType) => ({
       ...prevState,
       [`${depth}_${item.Name}`]: !prevState[`${depth}_${item.Name}`] || false,
      }));
     }}
    >
     <AccordionSummary
      sx={{
       minHeight: '40px',
       borderBottom: (themeParam: Theme) =>
        `1px solid ${themeParam.palette.kemek.lightestGray}`,
       '&.Mui-expanded': { minHeight: '40px' },
       '& .MuiAccordionSummary-content': {
        margin: 0,
        alignItems: 'center',
        '&.Mui-expanded': { margin: 0 },
       },
       //  p: 0,
       pl: depth > 1 ? depth * 0.8 : '12px',

       '.MuiButtonBase-root.MuiCheckbox-root': { p: '6px' },
      }}
      expandIcon={<IconAngleBottom />}
      aria-controls={`${item.Name}-content`}
      id={`${item.Name}-header`}
     >
      <Typography
       sx={{ fontWeight: 500, textOverflow: 'ellipsis', overflow: 'hidden' }}
      >
       {highlightText(item.Translation, searchValue, theme)}
      </Typography>
     </AccordionSummary>
     <AccordionDetails sx={{ p: 0 }}>
      <FormGroup
       sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        py: '4px',
        px: depth === 0 ? 0 : '4px',
        borderBottom: (themeParam: Theme) =>
         `1px solid ${themeParam.palette.kemek.lightestGray}`,
        width: '100%',
       }}
      >
       {item.InnerStructure?.map((value: TransformedStructure) => {
        const row = item.InnerStructure?.find((o) => o.Name === value.Name);
        if (!row) return null;

        const innerName = row.Name;
        if (['model', 'array'].includes(row.Type)) {
         return renderFields({
          item: row,
          pName: `${currentPath}.${innerName}`,
          depth,
          searchValue,
          handleSetPreset,
          expandedState,
          setExpandedState,
          theme,
         });
        }
        return (
         <PaletteDraggableField
          key={`all_${item.Name}.${innerName}.${row.Key}`}
          item={row}
          highlight={highlightText(row.Translation, searchValue, theme)}
          depth={depth}
         />
        );
       })}
      </FormGroup>
     </AccordionDetails>
    </Accordion>
   </Box>
  );
 }
 return (
  <Box sx={{ px: 1, py: 0.5 }} key={`draggable_${item.Key}`}>
   <PaletteDraggableField
    key={`draggable_inner_${item.Key}`}
    item={item}
    highlight={highlightText(item.Translation, searchValue, theme)}
    depth={depth}
   />
  </Box>
 );
};

const renderFieldsNoModels = ({
 item,
 searchValue,
 theme,
}: {
 item: TransformedStructure;
 searchValue: string;
 theme: Theme;
}) => (
 <Box sx={{ px: 1, py: 0.5 }} key={`draggable_${item.Key}`}>
  <PaletteDraggableField
   key={`draggable_inner_${item.Key}`}
   item={item}
   highlight={highlightText(item.Translation, searchValue, theme)}
   depth={0}
  />
 </Box>
);

const FieldsModalCols = ({
 type,
 items,
}: {
 type: 'sidebar' | 'edit';
 items: { root: TransformedStructure[]; selected: TransformedStructure[] };
}) => {
 const { t } = useTranslation();
 const theme = useTheme();

 const [searchValue, setSearchValue] = useState('');
 const [expandedState, setExpandedState] = useState<ExpandedStateType>({});

 const handleSetPreset = (columns: GridColDef[]) => {
  console.log('handleSetPreset', columns);
 };

 const filteredStructure = useMemo(
  () => filterStructureForSearch(items.root, items.selected, searchValue),
  [items, searchValue],
 );

 useEffect(() => {
  if (searchValue) {
   if (filteredStructure.length > 0) {
    const modelOrArrayNames = filterStructureForExpanded(filteredStructure);
    setExpandedState(
     modelOrArrayNames.reduce((acc, item) => {
      acc[item] = true;
      return acc;
     }, {} as ExpandedStateType),
    );
   }
  } else {
   // Collapse all accordions when search is cleared
   setExpandedState({});
  }
 }, [searchValue]); // filteredStructure

 const structureSorted = useMemo(
  () => sortStructure(filteredStructure),
  [filteredStructure],
 );

 const Sorted = structureSorted.map((item) =>
  type === 'sidebar'
   ? renderFields({
      item,
      pName: '',
      depth: 0,
      searchValue,
      handleSetPreset,
      expandedState,
      setExpandedState,
      theme,
     })
   : renderFieldsNoModels({
      item,
      searchValue,
      theme,
     }),
 );

 return (
  <Box
   sx={{
    borderRadius: '4px',
    border: (themeParam: Theme) =>
     `1px solid ${themeParam.palette.kemek.lightestGray}`,
    borderBottom: 0,
    width: { xs: '100%', md: 280 },
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
   }}
  >
   <Box sx={{ flexShrink: 0 }}>
    <FieldSearch
     searchValue={searchValue}
     setSearchValue={setSearchValue}
     label={t('fieldsModal.fieldsAll')}
    />
   </Box>
   <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>{Sorted}</Box>
  </Box>
 );
};
export default FieldsModalCols;
